import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

type MemberInfo = {
	registered: boolean;
	_id?: string;
	email?: string;
	name?: string;
	active?: boolean;
};

type FamilyInvite = {
	_id: string;
	inviter_phone: string;
	invitee_phone: string;
	status: 'pending' | 'registered';
	createdAt: string;
	inviter: MemberInfo;
	invitee: MemberInfo;
};

const STATUS_LABELS: Record<string, { text: string; color: 'success' | 'warning' | 'default' }> = {
	pending: { text: 'ממתין', color: 'warning' },
	registered: { text: 'נרשם/ה', color: 'success' },
};

const formatDate = (value?: string | null) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '—');

const csvEscape = (value: any) => {
	if (value === null || value === undefined) return '';
	const str = String(value);
	if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
	return str;
};

const downloadCsv = (rows: FamilyInvite[]) => {
	const header = [
		'טלפון מזמין',
		'מזמין רשום',
		'שם מזמין',
		'אימייל מזמין',
		'טלפון מוזמן',
		'מוזמן רשום',
		'שם מוזמן',
		'אימייל מוזמן',
		'סטטוס',
		'נוצר',
	];
	const lines = [header.join(',')];
	for (const r of rows) {
		lines.push(
			[
				r.inviter_phone,
				r.inviter?.registered ? 'כן' : 'לא',
				r.inviter?.name || '',
				r.inviter?.email || '',
				r.invitee_phone,
				r.invitee?.registered ? 'כן' : 'לא',
				r.invitee?.name || '',
				r.invitee?.email || '',
				STATUS_LABELS[r.status]?.text || r.status,
				formatDate(r.createdAt),
			]
				.map(csvEscape)
				.join(','),
		);
	}
	const csv = '﻿' + lines.join('\n'); // BOM for Excel Hebrew
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `family-invites-${moment().format('YYYY-MM-DD-HHmm')}.csv`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

const MemberCell = ({ phone, info }: { phone: string; info: MemberInfo }) => (
	<Box>
		<Typography variant="body2" dir="ltr" sx={{ textAlign: 'right' }}>
			{phone}
		</Typography>
		{info?.registered ? (
			<>
				<Typography variant="caption" display="block">
					{info.name || '—'}
				</Typography>
				<Typography variant="caption" color="text.secondary" display="block" dir="ltr" sx={{ textAlign: 'right' }}>
					{info.email || '—'}
				</Typography>
				<Chip
					size="small"
					label={info.active ? 'רשום • פעיל' : 'רשום • לא פעיל'}
					color={info.active ? 'success' : 'default'}
					sx={{ mt: 0.5 }}
				/>
			</>
		) : (
			<Chip size="small" label="לא רשום" variant="outlined" sx={{ mt: 0.5 }} />
		)}
	</Box>
);

const FamilyInvitesList = () => {
	const [data, setData] = useState<FamilyInvite[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0); // 0-based for TablePagination
	const [limit, setLimit] = useState(50);
	const [phone, setPhone] = useState('');
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const params = new URLSearchParams({
				page: String(page + 1),
				limit: String(limit),
				phone: search,
			});
			const response = await httpClient(`${constants.API_URL}admin/family-invites?${params.toString()}`, {
				method: 'GET',
			});
			const json = response.json as { data: FamilyInvite[]; total: number };
			setData(json.data || []);
			setTotal(json.total || 0);
		} catch (err: any) {
			setError('לא הצלחנו לטעון את הנתונים');
		} finally {
			setLoading(false);
		}
	}, [page, limit, search]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleSearch = () => {
		setPage(0);
		setSearch(phone.trim());
	};

	const handleExport = async () => {
		try {
			const params = new URLSearchParams({ page: '1', limit: '100000', phone: search });
			const response = await httpClient(`${constants.API_URL}admin/family-invites?${params.toString()}`, {
				method: 'GET',
			});
			const json = response.json as { data: FamilyInvite[] };
			downloadCsv(json.data || []);
		} catch (err: any) {
			setError('שגיאה בייצוא CSV');
		}
	};

	const stats = useMemo(() => {
		const registeredInvitees = data.filter(d => d.status === 'registered').length;
		return { registeredInvitees };
	}, [data]);

	return (
		<Box p={3} dir="rtl">
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h4">הזמנות בני משפחה</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<FaFileCsv />}
					onClick={handleExport}
					disabled={!total}
				>
					ייצוא CSV
				</Button>
			</Stack>

			<Stack direction="row" spacing={1} mb={2} alignItems="center">
				<TextField
					size="small"
					label="חיפוש לפי טלפון (מזמין/מוזמן)"
					value={phone}
					onChange={e => setPhone(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') handleSearch();
					}}
					sx={{ minWidth: 280 }}
				/>
				<Button variant="outlined" onClick={handleSearch}>
					חיפוש
				</Button>
				{search && (
					<Button
						variant="text"
						onClick={() => {
							setPhone('');
							setPage(0);
							setSearch('');
						}}
					>
						ניקוי
					</Button>
				)}
				<Chip label={`סה"כ: ${total}`} />
				<Chip label={`נרשמו (בעמוד): ${stats.registeredInvitees}`} color="success" />
			</Stack>

			{error && (
				<Typography color="error" mb={2}>
					{error}
				</Typography>
			)}

			<TableContainer component={Paper}>
				{loading && (
					<Stack alignItems="center" p={4}>
						<CircularProgress />
					</Stack>
				)}
				{!loading && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>מזמין</TableCell>
								<TableCell>מוזמן</TableCell>
								<TableCell>סטטוס</TableCell>
								<TableCell>נוצר</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.map(row => {
								const statusMeta = STATUS_LABELS[row.status];
								return (
									<TableRow key={row._id}>
										<TableCell>
											<MemberCell phone={row.inviter_phone} info={row.inviter} />
										</TableCell>
										<TableCell>
											<MemberCell phone={row.invitee_phone} info={row.invitee} />
										</TableCell>
										<TableCell>
											<Chip
												size="small"
												label={statusMeta?.text || row.status}
												color={statusMeta?.color || 'default'}
											/>
										</TableCell>
										<TableCell>{formatDate(row.createdAt)}</TableCell>
									</TableRow>
								);
							})}
							{!data.length && (
								<TableRow>
									<TableCell colSpan={4} align="center">
										אין הזמנות
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				)}
				<TablePagination
					component="div"
					count={total}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={limit}
					onRowsPerPageChange={e => {
						setLimit(parseInt(e.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[25, 50, 100, 200]}
					labelRowsPerPage="שורות בעמוד"
				/>
			</TableContainer>
		</Box>
	);
};

export default FamilyInvitesList;
